import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { Copy, Link as LinkIcon, QrCode, Send, Users } from "lucide-react"
import { getApiBaseUrl } from "../../config/apiBase"
import "../../styles/components/ChurchOnboarding.css"

const isFellowshipGroup = (group) => {
  const type = String(group?.groupType || group?.GroupType || "").trim().toLowerCase()
  return type === "denomination" || type === "network"
}

const isChurchGroup = (group) => {
  const type = String(group?.groupType || group?.GroupType || "").trim().toLowerCase()
  return type === "congregation" || type === "church"
}

const modeLabel = {
  Open: "Open connection",
  Application: "Membership request",
}

export default function ChurchInvitePanel({ group }) {
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth0()
  const [links, setLinks] = useState([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState("")
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [membershipMode, setMembershipMode] = useState("Open")
  const [label, setLabel] = useState("")
  const apiUrl = getApiBaseUrl()
  const groupId = group?.id || group?.groupId
  const canInviteCongregation = isChurchGroup(group)
  const canInviteFellowship = isFellowshipGroup(group)

  const defaultMode = useMemo(() => {
    const eligible = String(group?.eligibleTo || "").toLowerCase()
    return eligible.includes("application") ? "Application" : "Open"
  }, [group?.eligibleTo])

  useEffect(() => {
    setMembershipMode(defaultMode)
  }, [defaultMode])

  const loadLinks = useCallback(async () => {
    if (!groupId || !isAuthenticated || authLoading) {
      return
    }

    setLoading(true)
    setError("")
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const response = await fetch(`${apiUrl}/api/ChurchInvites/groups/${groupId}/links`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.status === 403) {
        setNotice("Invite tools are available to church representatives for this group.")
        setLinks([])
        return
      }

      if (!response.ok) {
        throw new Error("Could not load church invite links.")
      }

      setLinks(await response.json())
      setNotice("")
    } catch (err) {
      setError(err.message || "Could not load church invite links.")
    } finally {
      setLoading(false)
    }
  }, [apiUrl, authLoading, getAccessTokenSilently, groupId, isAuthenticated])

  useEffect(() => {
    loadLinks()
  }, [loadLinks])

  const createInvite = async (inviteType) => {
    setCreating(inviteType)
    setError("")
    setNotice("")
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const response = await fetch(`${apiUrl}/api/ChurchInvites/groups/${groupId}/links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          inviteType,
          membershipMode,
          label: label.trim() || null,
        }),
      })

      if (!response.ok) {
        throw new Error(response.status === 409
          ? "This invite type is not available for this group."
          : "Could not create this invite link.")
      }

      const nextLink = await response.json()
      setLinks((current) => {
        const withoutDuplicate = current.filter((item) => item.id !== nextLink.id)
        return [nextLink, ...withoutDuplicate]
      })
      setLabel("")
      setNotice("Invite link is ready to share.")
    } catch (err) {
      setError(err.message || "Could not create this invite link.")
    } finally {
      setCreating("")
    }
  }

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url)
      setNotice("Invite link copied.")
    } catch {
      setNotice("Select and copy the invite link below.")
    }
  }

  if (!canInviteCongregation && !canInviteFellowship) return null

  return (
    <section className="church-invite-panel">
      <div className="church-invite-heading">
        <span className="group-section-kicker">
          <Send className="h-4 w-4" /> Invite flow
        </span>
        <h3>Invite congregation and church networks</h3>
        <p>
          Create a link that can be shared from the pulpit, in a bulletin, by text, or as a QR code.
          Churches that require a covenant or conduct agreement can route people through membership requests.
        </p>
      </div>

      <div className="church-invite-controls">
        <label>
          Membership flow
          <select value={membershipMode} onChange={(event) => setMembershipMode(event.target.value)}>
            <option value="Open">Open to connect</option>
            <option value="Application">Request membership first</option>
          </select>
        </label>
        <label>
          Link label
          <input
            type="text"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Sunday bulletin, youth group, outreach night"
          />
        </label>
      </div>

      <div className="church-invite-actions">
        {canInviteCongregation && (
          <button type="button" onClick={() => createInvite("Congregation")} disabled={Boolean(creating)}>
            <Users size={17} aria-hidden="true" />
            {creating === "Congregation" ? "Creating..." : "Invite congregation"}
          </button>
        )}
        {canInviteFellowship && (
          <button type="button" onClick={() => createInvite("Fellowship")} disabled={Boolean(creating)}>
            <LinkIcon size={17} aria-hidden="true" />
            {creating === "Fellowship" ? "Creating..." : "Invite another church"}
          </button>
        )}
      </div>

      {canInviteFellowship && (
        <p className="church-invite-note">
          Fellowship invites connect peer churches into a denomination or fellowship dashboard. They do not make one local church the parent of another.
        </p>
      )}

      {loading && <div className="church-invite-status">Loading existing links...</div>}
      {notice && <div className="church-invite-status">{notice}</div>}
      {error && <div className="church-invite-error" role="alert">{error}</div>}

      <div className="church-invite-link-grid">
        {links.map((link) => (
          <article key={link.id} className="church-invite-link-card">
            <div>
              <span>{link.inviteType === "Fellowship" ? "Church fellowship invite" : "Congregation invite"}</span>
              <strong>{link.label || modeLabel[link.membershipMode] || link.membershipMode}</strong>
              <small>{link.useCount} uses</small>
            </div>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=164x164&margin=8&data=${encodeURIComponent(link.inviteUrl)}`}
              alt={`QR code for ${link.inviteType.toLowerCase()} invite`}
            />
            <div className="church-invite-url">
              <QrCode size={16} aria-hidden="true" />
              <code>{link.inviteUrl}</code>
            </div>
            <button type="button" onClick={() => copyLink(link.inviteUrl)}>
              <Copy size={16} aria-hidden="true" />
              Copy link
            </button>
          </article>
        ))}
      </div>
    </section>
  )
}
